import type { ReplyAllDraftRequest } from "@src/load-agent/types/emailDraft";
import { getLoadAgentApiBaseUrl } from "@src/load-agent/http/LoadAgentHttpTransport";

const MAX_REPLY_BASE64_BYTES = 4 * 1024 * 1024;

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";

  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, Math.min(i + chunk, bytes.length));

    binary += String.fromCharCode.apply(null, Array.from(slice));
  }
  return btoa(binary);
}

function isPdfMagic(buf: ArrayBuffer): boolean {
  const u = new Uint8Array(buf.slice(0, 4));

  return u[0] === 0x25 && u[1] === 0x50 && u[2] === 0x44 && u[3] === 0x46;
}

export function isDirectExchangeAttachmentUrl(url: string): boolean {
  try {
    const u = new URL(url);

    if (u.protocol !== "https:") return false;
    if (u.search.includes("X-Amz-Credential=") || u.search.includes("X-Amz-Algorithm=")) {
      return true;
    }
    return u.hostname.endsWith(".amazonaws.com");
  } catch {
    return false;
  }
}

type TaskpaneWindow = Pick<Window, "location">;

export function coerceAttachmentUrlForTaskpaneFetch(
  url: string,
  win: TaskpaneWindow | undefined = typeof window !== "undefined" ? window : undefined
): string {
  if (!win?.location?.origin) {
    return url;
  }
  const pageOrigin = win.location.origin;
  let parsed: URL;

  try {
    parsed = new URL(url.trim());
  } catch {
    try {
      parsed = new URL(url.trim(), pageOrigin);
    } catch {
      return url;
    }
  }
  const pathAndQuery = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  const looksLikeHostedDoc =
    pathAndQuery.includes("/documents/demo-bol") || pathAndQuery.includes("/documents/proxy");

  if (!looksLikeHostedDoc) {
    return url;
  }
  if (pathAndQuery.startsWith("/api/")) {
    return `${pageOrigin}${pathAndQuery}`;
  }
  const api = getLoadAgentApiBaseUrl();

  if (api && pathAndQuery.startsWith("/documents/")) {
    const apiRoot = api.endsWith("/") ? api.slice(0, -1) : api;

    return `${pageOrigin}${apiRoot}${pathAndQuery}`;
  }
  return `${pageOrigin}${pathAndQuery}`;
}

function buildBase64ReplyAttachment(name: string, base64: string): Record<string, unknown> {
  const attachmentType = Office.MailboxEnums?.AttachmentType;
  const type = attachmentType?.Base64 != null ? attachmentType.Base64 : "base64";

  return {
    type,
    name,
    base64file: base64,
  };
}

async function buildOfficeReplyAttachments(
  attachments: {
    name: string;
    url: string;
  }[]
): Promise<Record<string, unknown>[]> {
  const fileType = Office.MailboxEnums?.AttachmentType?.File ?? "file";
  const out: Record<string, unknown>[] = [];

  for (const a of attachments) {
    const fetchUrl = coerceAttachmentUrlForTaskpaneFetch(a.url);

    if (isDirectExchangeAttachmentUrl(fetchUrl)) {
      out.push({ type: fileType, name: a.name, url: fetchUrl });
      continue;
    }
    try {
      const res = await fetch(fetchUrl, { credentials: "include", mode: "cors" });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const buf = await res.arrayBuffer();

      if (buf.byteLength === 0 || buf.byteLength > MAX_REPLY_BASE64_BYTES) {
        throw new Error("attachment empty or too large for Base64 reply");
      }
      if (!isPdfMagic(buf)) {
        const ct = res.headers.get("content-type") ?? "";

        throw new Error(`Expected PDF (Content-Type: ${ct || "unknown"})`);
      }
      out.push(buildBase64ReplyAttachment(a.name, arrayBufferToBase64(buf)));
    } catch {
      out.push({
        type: fileType,
        name: a.name,
        url: fetchUrl,
      });
    }
  }
  return out;
}

export async function openOutlookReplyAllWithAttachments(
  request: ReplyAllDraftRequest
): Promise<void> {
  const item = Office.context?.mailbox?.item;

  if (!item) {
    throw new Error("No email item found — Office.js mailbox context unavailable");
  }
  const raw = request.attachments ?? [];
  const officeAttachments = raw.length > 0 ? await buildOfficeReplyAttachments(raw) : [];
  const formData: Record<string, unknown> = { htmlBody: request.htmlBody };

  if (officeAttachments.length > 0) {
    formData.attachments = officeAttachments;
  }
  // OWA has a bug where displayReplyAllFormAsync sets the recipient to the current
  // user (self) instead of the original sender. Fall back to displayReplyFormAsync
  // on OWA so the reply is correctly addressed to the sender.
  const isOWA =
    (Office.context?.mailbox as { diagnostics?: { hostName?: string } })?.diagnostics?.hostName ===
    "OutlookWebApp";

  type ReplyMethod = (
    formData: Record<string, unknown>,
    callback: (result: Office.AsyncResult<void>) => void
  ) => void;
  const replyMethod: ReplyMethod = isOWA
    ? (item as unknown as { displayReplyFormAsync: ReplyMethod }).displayReplyFormAsync.bind(item)
    : (item.displayReplyAllFormAsync as unknown as ReplyMethod).bind(item);

  return new Promise<void>((resolve, reject) => {
    replyMethod(formData, (asyncResult: Office.AsyncResult<void>) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        if (officeAttachments.length > 0) {
          replyMethod({ htmlBody: request.htmlBody }, (retryResult) => {
            if (retryResult.status === Office.AsyncResultStatus.Failed) {
              reject(
                new Error(
                  `Failed to create email draft: ${retryResult.error?.message ?? "unknown"}`
                )
              );
            } else {
              resolve();
            }
          });
        } else {
          reject(
            new Error(`Failed to create email draft: ${asyncResult.error?.message ?? "unknown"}`)
          );
        }
      } else {
        resolve();
      }
    });
  });
}
